import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { Sun, Moon, Globe, Save, Palette, RefreshCw, Lock, ShieldCheck } from "lucide-react";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import ChangePasswordModal from "../components/ui/ChangePasswordModal";

// --- Helper Functions ---

const colorNameToHex = (colorName) => {
  const mapping = {
    "slate-900": "#0f172a",
    "slate-800": "#1e293b",
    "slate-700": "#334155",
    "slate-100": "#f1f5f9",
    "slate-50": "#f8fafc",
    white: "#ffffff",
  };
  return mapping[colorName] || null;
};

const parseGradient = (className, defaultColors) => {
  if (typeof className !== "string") return defaultColors;
  const result = { ...defaultColors };
  const classes = className.split(" ");
  const stops = ["from", "via", "to"];

  stops.forEach((stop) => {
    const prefix = `${stop}-`;
    const stopClass = classes.find((c) => c.startsWith(prefix));

    if (stopClass) {
      if (stopClass.includes("[#")) {
        const hex = stopClass.slice(prefix.length + 1, -1);
        result[stop] = hex.startsWith("#") ? hex : `#${hex}`;
      } else {
        const colorName = stopClass.substring(prefix.length);
        const hex = colorNameToHex(colorName);
        if (hex) result[stop] = hex;
      }
    }
  });
  return result;
};

const getColorFromClass = (className, defaultColor = "#000000") => {
  if (typeof className !== "string") return defaultColor;
  const classes = className.split(" ");
  const colorClass = classes.find(
    (c) =>
      c.startsWith("bg-[") ||
      c.startsWith("text-[") ||
      c.startsWith("border-[") ||
      c.match(/(bg|text|border)-[a-z]+-\d+/),
  );

  if (colorClass) {
    if (colorClass.includes("[#")) {
      const hex = colorClass.substring(
        colorClass.indexOf("[") + 1,
        colorClass.length - 1,
      );
      return hex.startsWith("#") ? hex : `#${hex}`;
    } else {
      const parts = colorClass.split("-");
      if (parts.length > 2) {
        const colorName = `${parts[1]}-${parts[2]}`;
        return colorNameToHex(colorName) || defaultColor;
      }
    }
  }
  return defaultColor;
};

// --- Child Components ---

const ColorPicker = ({ label, property, value, onChange }) => {
  const { colors } = useTheme();
  const prefix = property.match(/^(hover:)?(bg|text|border)/)?.[0] || "bg";

  const handleColorChange = (e) => {
    const hexValue = e.target.value;
    onChange(property, `${prefix}-[${hexValue}]`);
  };

  return (
    <div>
      <label
        className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
      >
        {label}
      </label>
      <div className="flex items-center space-x-2">
        <input
          type="color"
          value={getColorFromClass(value)}
          onChange={handleColorChange}
          className="w-10 h-10 p-1 border-none rounded-lg cursor-pointer"
          style={{ backgroundColor: "transparent" }}
        />
        <input
          type="text"
          value={value}
          readOnly
          className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} ${colors.inputFocus} text-xs`}
        />
      </div>
    </div>
  );
};

const GradientPicker = ({ label, property, value, onChange }) => {
  const { colors } = useTheme();
  const [gradient, setGradient] = useState({
    from: "#000000",
    via: "#000000",
    to: "#000000",
  });

  useEffect(() => {
    const defaultColors = { from: "#0f172a", via: "#1e293b", to: "#0f172a" };
    setGradient(parseGradient(value, defaultColors));
  }, [value]);

  const handleColorChange = (stop, hexValue) => {
    const newGradient = { ...gradient, [stop]: hexValue };
    setGradient(newGradient);
    const newClassName = `bg-gradient-to-b from-[${newGradient.from}] via-[${newGradient.via}] to-[${newGradient.to}]`;
    onChange(property, newClassName);
  };

  return (
    <div className="md:col-span-2">
      <label
        className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
      >
        {label}
      </label>
      <div
        className="grid grid-cols-3 gap-4 p-4 rounded-lg"
        style={{
          background: `linear-gradient(to bottom, ${gradient.from}, ${gradient.via}, ${gradient.to})`,
        }}
      >
        {["from", "via", "to"].map((stop) => (
          <div key={stop}>
            <span className="text-xs font-semibold text-white capitalize drop-shadow-md">
              {stop}
            </span>
            <input
              type="color"
              value={gradient[stop]}
              onChange={(e) => handleColorChange(stop, e.target.value)}
              className="w-full h-10 p-1 mt-1 border-none rounded-lg cursor-pointer"
              style={{ backgroundColor: "transparent" }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main Settings Page Component ---

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const {
    theme,
    toggleTheme,
    density,
    setDensity,
    colors,
    saveThemeConfig,
    resetTheme,
  } = useTheme();
  const [timezone, setTimezone] = useState("");
  const [timezones, setTimezones] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [themeConfig, setThemeConfig] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  useEffect(() => {
    setTimezones(Intl.supportedValuesOf("timeZone"));
    setTimezone(
      localStorage.getItem("user-timezone") ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
  }, []);

  useEffect(() => {
    setThemeConfig(colors);
  }, [colors]);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  const handleThemeConfigChange = (property, value) => {
    setThemeConfig((prev) => ({ ...prev, [property]: value }));
  };

  const handleSaveSettings = () => {
    localStorage.setItem("user-timezone", timezone);
    saveThemeConfig(themeConfig);
    setShowConfirmation(true);
    setTimeout(() => setShowConfirmation(false), 3000);
  };

  const handleResetTheme = () => {
    resetTheme();
    setShowResetConfirm(false);
  };

  return (
    <>
      <div
        className={`p-4 md:p-8 ${colors.bgPrimary} min-h-screen transition-colors duration-300`}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className={`text-2xl sm:text-3xl font-bold ${colors.textPrimary} mb-6 sm:mb-8`}>
            {t("settingsPage.title")}
          </h1>

          <div
            className={`rounded-2xl shadow-2xl overflow-hidden ${colors.cardBg} ${colors.cardBorder} border`}
          >
            <div className={`p-6 border-b ${colors.border}`}>
              <h2
                className={`text-xl font-semibold ${colors.textPrimary} mb-4`}
              >
                {t("settingsPage.appearance")}
              </h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className={`${colors.textSecondary}`}>
                    {t("settingsPage.theme")}
                  </span>
                  <div
                    className={`flex items-center space-x-2 p-1 rounded-full ${colors.inputBg}`}
                  >
                    <button
                      onClick={() => theme === "dark" && toggleTheme()}
                      className={`p-2 rounded-full transition-all duration-300 ${theme === "light" ? "bg-blue-500 text-white shadow-lg" : `${colors.textMuted} hover:bg-slate-700`}`}
                    >
                      <Sun className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => theme === "light" && toggleTheme()}
                      className={`p-2 rounded-full transition-all duration-300 ${theme === "dark" ? "bg-blue-500 text-white shadow-lg" : `${colors.textMuted} hover:bg-slate-200`}`}
                    >
                      <Moon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className={`${colors.textSecondary}`}>
                    Display Density
                  </span>
                  <div
                    className={`flex items-center space-x-1 p-1 rounded-xl ${colors.inputBg} w-max`}
                  >
                    <button
                      onClick={() => setDensity("standard")}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${density === "standard" ? "bg-blue-500 text-white shadow-md" : `${colors.textSecondary} hover:bg-white/5`}`}
                    >
                      Standard
                    </button>
                    <button
                      onClick={() => setDensity("compact")}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${density === "compact" ? "bg-blue-500 text-white shadow-md" : `${colors.textSecondary} hover:bg-white/5`}`}
                    >
                      Compact
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* "Customize Theme" section temporarily hidden — its overrides
                were leaking dark-mode colors into light mode and corrupting the
                page. Will return when per-mode storage is implemented. */}
            {false && (
            <div className={`p-6 border-b ${colors.border}`}>
              <div className="flex justify-between items-center mb-4">
                <h2
                  className={`text-xl font-semibold ${colors.textPrimary} flex items-center`}
                >
                  <Palette className="w-5 h-5 mr-2" />
                  {t("settingsPage.customizeTheme")} (
                  {theme === "light" ? "Light" : "Dark"})
                </h2>
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className={`flex items-center space-x-2 text-sm ${colors.textSecondary} hover:text-red-400 transition-colors`}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>{t("settingsPage.resetTheme")}</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <GradientPicker
                  label="Primary Background"
                  property="bgPrimary"
                  value={themeConfig.bgPrimary || ""}
                  onChange={handleThemeConfigChange}
                />
                <ColorPicker
                  label="Primary Text"
                  property="textPrimary"
                  value={themeConfig.textPrimary || ""}
                  onChange={handleThemeConfigChange}
                />
                <ColorPicker
                  label="Header/Sidebar BG"
                  property="bgSecondary"
                  value={themeConfig.bgSecondary || ""}
                  onChange={handleThemeConfigChange}
                />
                <ColorPicker
                  label="Card Background"
                  property="cardBg"
                  value={themeConfig.cardBg || ""}
                  onChange={handleThemeConfigChange}
                />
                <ColorPicker
                  label="Border Color"
                  property="border"
                  value={themeConfig.border || ""}
                  onChange={handleThemeConfigChange}
                />
              </div>
            </div>
            )}

            <div className={`p-6 border-b ${colors.border}`}>
              <h2
                className={`text-xl font-semibold ${colors.textPrimary} mb-4`}
              >
                {t("settingsPage.localization")}
              </h2>
              <div className="flex items-center justify-between">
                <span className={`${colors.textSecondary}`}>Language</span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleLanguageChange("en")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold ${currentLanguage.startsWith("en") ? "bg-blue-500 text-white" : colors.inputBg}`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => handleLanguageChange("tr")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold ${currentLanguage === "tr" ? "bg-blue-500 text-white" : colors.inputBg}`}
                  >
                    Türkçe
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <h2
                className={`text-xl font-semibold ${colors.textPrimary} mb-4`}
              >
                {t("settingsPage.timezone")}
              </h2>
              <div className="relative">
                <label
                  htmlFor="timezone"
                  className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
                >
                  {t("settingsPage.timezone")}
                </label>
                <div className="relative">
                  <Globe
                    className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${colors.textMuted}`}
                  />
                  <select
                    id="timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg appearance-none ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} ${colors.inputFocus}`}
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Güvenlik */}
            <div className="p-6">
              <h2 className={`text-xl font-semibold ${colors.textPrimary} mb-4 flex items-center`}>
                <ShieldCheck className="w-5 h-5 mr-2" />
                Güvenlik
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div>
                  <p className={`text-sm font-medium ${colors.textPrimary}`}>Hesap Şifresi</p>
                  <p className={`text-[12px] ${colors.textTertiary} mt-0.5`}>
                    Hesabınızın şifresini değiştirmek için tıklayın.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowChangePassword(true)}
                  className={`w-full sm:w-auto justify-center flex items-center gap-2 px-4 py-2 rounded-lg border ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} ${colors.btnSecondaryBg} ${colors.btnSecondaryHover} text-sm font-semibold transition-colors shrink-0`}
                >
                  <Lock className="w-4 h-4" />
                  <span>Şifre Değiştir</span>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSaveSettings}
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
            >
              <Save className="w-5 h-5" />
              <span>{t("settingsPage.saveSettings")}</span>
            </button>
          </div>

          <ChangePasswordModal
            isOpen={showChangePassword}
            onClose={() => setShowChangePassword(false)}
          />

          {showConfirmation && (
            <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg text-center animate-in fade-in">
              Settings saved successfully!
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleResetTheme}
        title="Reset Theme"
        description="Are you sure you want to reset the theme to its default settings? This action cannot be undone."
      />
    </>
  );
};

export default SettingsPage;
